import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { cn } from '@/lib/cn';

/**
 * ArticleBodyRenderer — the single component responsible for safely
 * rendering an article's `body` HTML in both the admin preview pane
 * and the public reader view.
 *
 * Why HTML, not markdown: articles are authored by pasting in
 * AI-generated HTML with layout and infographics (SVG, inline styles,
 * custom classes). Markdown renderers normalize everything to their
 * own schema and strip the layout signal. We keep the source raw and
 * sanitize just before injection.
 *
 * Sanitization policy (DOMPurify):
 *   - ALLOW: all common layout/inline tags, full SVG subtree, inline
 *     `style`, `class`, `id`, and `data-*` attributes, author-provided
 *     `<style>` blocks (so AI infographics with scoped CSS still work).
 *   - BLOCK: `<script>`, `<iframe>`, `<object>`, `<embed>`, all `on*`
 *     event handlers, and `javascript:` URLs. DOMPurify handles these
 *     by default; we just enable the extras we want on top.
 *
 * Typographic defaults live in `src/styles/index.css` under the
 * `.article-body` class so unstyled tags still read well. Inline styles
 * on pasted content always win via normal CSS specificity.
 *
 * Note: only authenticated admins can author articles. DOMPurify is
 * defense in depth, not the only line of defense.
 */
export interface ArticleBodyRendererProps {
  /** Raw HTML string. Sanitized before render. */
  html: string;
  className?: string;
}

/**
 * DOMPurify configuration. Kept as a module-level constant so each
 * render doesn't rebuild the config object.
 *
 * USE_PROFILES html:true + svg:true enables the default HTML and SVG
 * tag/attribute allowlists. ADD_TAGS / ADD_ATTR then layer on the bits
 * we explicitly want that aren't in the default list.
 */
const SANITIZE_CONFIG: Config = {
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
  // Author-provided <style> blocks are how most AI infographics ship
  // scoped CSS. They're allowed here; inline event handlers are not.
  ADD_TAGS: ['style'],
  ADD_ATTR: [
    'style',
    'class',
    'id',
    'target',
    // SVG-specific attributes not in the default profile
    'viewBox',
    'xmlns',
    'preserveAspectRatio',
  ],
  // Belt-and-suspenders: never let anything add href/src pointing at
  // javascript:. DOMPurify filters these by default; this makes the
  // intent explicit in the config.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp|#|\/):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // `data-*` attributes pass through by default.
};

export function ArticleBodyRenderer({ html, className }: ArticleBodyRendererProps) {
  const sanitized = useMemo(() => {
    if (!html) return '';
    // DOMPurify.sanitize can return `TrustedHTML` when the global Trusted
    // Types policy is active; coerce through `unknown` to keep the string
    // shape React's `dangerouslySetInnerHTML` expects.
    const out = DOMPurify.sanitize(html, SANITIZE_CONFIG);
    return typeof out === 'string' ? out : String(out);
  }, [html]);

  return (
    <div
      className={cn('article-body', className)}
      // Sanitized immediately above — safe to inject.
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
